import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  RefreshCw,
  Clock,
  Mail,
  User,
  Phone,
  AlertCircle,
  XCircle,
  CheckCircle,
  MessageCircle,
  Sparkles,
  ChevronDown,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { AdminTabs } from './AdminTabs';

const STATUS_CONFIG = {
  new: {
    label: 'New',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: Sparkles,
  },
  contacted: {
    label: 'Contacted',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Mail,
  },
  closed: {
    label: 'Closed',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle,
  },
};

export const AdminInquiriesPage = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [updatingId, setUpdatingId] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const fetchInquiries = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/inquiries`);
      if (!res.ok) throw new Error('Failed to fetch inquiries');
      const data = await res.json();
      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setInquiries(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    setOpenDropdown(null);
    try {
      const res = await fetch(`${backendUrl}/api/inquiries/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update inquiry');
      const updated = await res.json();
      setInquiries((prev) => prev.map((i) => (i.id === id ? updated : i)));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    setUpdatingId(id);
    setOpenDropdown(null);
    try {
      const res = await fetch(`${backendUrl}/api/inquiries/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete inquiry');
      setInquiries((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  useEffect(() => {
    const interval = setInterval(fetchInquiries, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const close = () => setOpenDropdown(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  const filtered = filter === 'all' ? inquiries : inquiries.filter((i) => i.status === filter);
  const newCount = inquiries.filter((i) => i.status === 'new').length;
  const contactedCount = inquiries.filter((i) => i.status === 'contacted').length;
  const closedCount = inquiries.filter((i) => i.status === 'closed').length;

  return (
    <div className="min-h-screen bg-background" data-testid="page-admin-inquiries">
      {/* Header */}
      <div className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="touch-target rounded-full hover:bg-secondary transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            <div>
              <h1 className="text-xl font-display text-foreground">Artwork Inquiries</h1>
              <p className="text-xs text-muted-foreground">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </p>
            </div>
          </div>

          <Button
            onClick={fetchInquiries}
            variant="outline"
            size="sm"
            disabled={loading}
            data-testid="refresh-inquiries-btn"
          >
            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      <AdminTabs />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats cards / filters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <button
            type="button"
            className={cn(
              'bg-card border rounded-lg p-4 text-left transition-all',
              filter === 'all'
                ? 'border-accent ring-1 ring-accent'
                : 'border-border hover:border-slate-muted'
            )}
            onClick={() => setFilter('all')}
            data-testid="inquiry-filter-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-foreground" />
              </div>
              <div>
                <p className="text-2xl font-medium text-foreground">{inquiries.length}</p>
                <p className="text-xs text-muted-foreground">Total Inquiries</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            className={cn(
              'bg-card border rounded-lg p-4 text-left transition-all',
              filter === 'new'
                ? 'border-yellow-500 ring-1 ring-yellow-500'
                : 'border-border hover:border-slate-muted'
            )}
            onClick={() => setFilter('new')}
            data-testid="inquiry-filter-new"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-medium text-foreground">{newCount}</p>
                <p className="text-xs text-muted-foreground">New / Unread</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            className={cn(
              'bg-card border rounded-lg p-4 text-left transition-all',
              filter === 'contacted'
                ? 'border-blue-500 ring-1 ring-blue-500'
                : 'border-border hover:border-slate-muted'
            )}
            onClick={() => setFilter('contacted')}
            data-testid="inquiry-filter-contacted"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-medium text-foreground">{contactedCount}</p>
                <p className="text-xs text-muted-foreground">Contacted</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            className={cn(
              'bg-card border rounded-lg p-4 text-left transition-all',
              filter === 'closed'
                ? 'border-green-500 ring-1 ring-green-500'
                : 'border-border hover:border-slate-muted'
            )}
            onClick={() => setFilter('closed')}
            data-testid="inquiry-filter-closed"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-medium text-foreground">{closedCount}</p>
                <p className="text-xs text-muted-foreground">Closed</p>
              </div>
            </div>
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && inquiries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <RefreshCw className="w-8 h-8 text-muted-foreground animate-spin mb-4" />
            <p className="text-muted-foreground">Loading inquiries...</p>
          </div>
        )}

        {/* Empty */}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="inquiries-empty">
            <MessageCircle className="w-16 h-16 text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-display text-foreground mb-2">
              {filter === 'all' ? 'No inquiries yet' : `No ${filter} inquiries`}
            </h2>
            <p className="text-muted-foreground max-w-md">
              {filter === 'all'
                ? "Inquiries from collectors interested in originals will land here. You'll see them as soon as anyone hits 'Inquire about this piece'."
                : 'Try a different filter to see other inquiries.'}
            </p>
          </div>
        )}

        {/* List */}
        {filtered.length > 0 && (
          <div className="space-y-4">
            {filtered.map((inq) => {
              const cfg = STATUS_CONFIG[inq.status] || STATUS_CONFIG.new;
              const StatusIcon = cfg.icon;
              const isExpanded = expandedId === inq.id;
              const mailtoBody = encodeURIComponent(
                `Hi ${inq.name.split(' ')[0]},\n\nThank you for your interest in "${inq.productTitle}". `
              );
              const mailtoSubject = encodeURIComponent(
                `Re: Inquiry — ${inq.productTitle}`
              );

              return (
                <div
                  key={inq.id}
                  className={cn(
                    'bg-card border rounded-lg overflow-hidden transition-opacity',
                    inq.status === 'new' ? 'border-yellow-200' : 'border-border',
                    updatingId === inq.id && 'opacity-50'
                  )}
                  data-testid={`inquiry-card-${inq.id}`}
                >
                  {/* Header */}
                  <div
                    className={cn(
                      'px-4 py-3 flex items-center justify-between',
                      inq.status === 'new' ? 'bg-yellow-50' : 'bg-secondary/30'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-foreground text-sm">
                          {inq.productTitle}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(inq.createdAt)}
                        </p>
                      </div>
                    </div>
                    <span
                      className={cn(
                        'text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1',
                        cfg.color
                      )}
                      data-testid={`inquiry-${inq.id}-status`}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {cfg.label}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="p-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                          Collector
                        </h4>
                        <div className="space-y-1">
                          <p className="text-sm text-foreground flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground" />
                            {inq.name}
                          </p>
                          <p className="text-sm text-foreground flex items-center gap-2">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <a
                              href={`mailto:${inq.email}?subject=${mailtoSubject}&body=${mailtoBody}`}
                              className="hover:underline"
                              data-testid={`inquiry-${inq.id}-email-link`}
                            >
                              {inq.email}
                            </a>
                          </p>
                          {inq.phone && (
                            <p className="text-sm text-foreground flex items-center gap-2">
                              <Phone className="w-4 h-4 text-muted-foreground" />
                              <a href={`tel:${inq.phone}`} className="hover:underline">
                                {inq.phone}
                              </a>
                            </p>
                          )}
                          {inq.productSlug && (
                            <p className="text-sm text-foreground flex items-center gap-2 pt-1">
                              <ExternalLink className="w-4 h-4 text-muted-foreground" />
                              <a
                                href={`/products/${inq.productSlug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs uppercase tracking-wider hover:underline"
                              >
                                View piece
                              </a>
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                          Message
                        </h4>
                        <p
                          className={cn(
                            'text-sm text-foreground whitespace-pre-wrap leading-relaxed',
                            !isExpanded && 'line-clamp-4'
                          )}
                          data-testid={`inquiry-${inq.id}-message`}
                        >
                          {inq.message}
                        </p>
                        {inq.message.length > 220 && (
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : inq.id)}
                            className="mt-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
                            data-testid={`inquiry-${inq.id}-toggle-expand`}
                          >
                            {isExpanded ? 'Show less' : 'Read more'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
                      <a
                        href={`mailto:${inq.email}?subject=${mailtoSubject}&body=${mailtoBody}`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider border border-border rounded hover:bg-secondary transition-colors"
                        data-testid={`inquiry-${inq.id}-reply-btn`}
                      >
                        <Mail className="w-3.5 h-3.5" />
                        Reply by email
                      </a>

                      <div className="flex items-center gap-2">
                        {inq.status === 'new' && (
                          <Button
                            size="sm"
                            onClick={() => updateStatus(inq.id, 'contacted')}
                            disabled={updatingId === inq.id}
                            className="text-xs bg-blue-600 hover:bg-blue-700"
                            data-testid={`inquiry-${inq.id}-mark-contacted`}
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            Mark as Contacted
                          </Button>
                        )}
                        {inq.status === 'contacted' && (
                          <Button
                            size="sm"
                            onClick={() => updateStatus(inq.id, 'closed')}
                            disabled={updatingId === inq.id}
                            className="text-xs bg-green-600 hover:bg-green-700"
                            data-testid={`inquiry-${inq.id}-mark-closed`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Mark as Closed
                          </Button>
                        )}

                        <div className="relative">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(openDropdown === inq.id ? null : inq.id);
                            }}
                            disabled={updatingId === inq.id}
                            data-testid={`inquiry-${inq.id}-more`}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>

                          {openDropdown === inq.id && (
                            <div
                              className="absolute right-0 mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-10"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="py-1">
                                {Object.entries(STATUS_CONFIG).map(([status, c]) => (
                                  <button
                                    key={status}
                                    onClick={() => updateStatus(inq.id, status)}
                                    disabled={inq.status === status}
                                    className={cn(
                                      'w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-secondary transition-colors',
                                      inq.status === status && 'opacity-50 cursor-not-allowed'
                                    )}
                                  >
                                    <c.icon className="w-4 h-4" />
                                    Set {c.label}
                                    {inq.status === status && (
                                      <CheckCircle className="w-3 h-3 ml-auto text-green-500" />
                                    )}
                                  </button>
                                ))}
                                <div className="border-t border-border my-1" />
                                <button
                                  onClick={() => deleteInquiry(inq.id)}
                                  className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-red-600 hover:bg-red-50 transition-colors"
                                  data-testid={`inquiry-${inq.id}-delete`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete Inquiry
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInquiriesPage;
